//@flow
import PjpChartDataBuilder from 'common/entity/pjpMaster/pjpInsights/charts/PjpChartDataBuilder';
import PjpMetricCardDecider from './PjpMetricCardDecider';
import _ from 'lodash';

import NumberUtils from 'common/utils/NumberUtils';
import DifferentiableColorPicker from 'common/utils/DifferentiableColorPicker';

import type { List } from 'immutable';
import type {
    PjpCartesianChartDataType,
    StacksType
} from 'common/entity/pjpMaster/pjpDataTypes/insights/PjpCartesianChartDataType';
import type { PjpMetricGraph } from 'common/models/internal/PjpMetricGraph';
import type { PjpGraphResponse } from 'common/models/internal/PjpGraphResponse';
import type { PjpMetricDataItem } from 'common/models/internal/PjpMetricResponse';

export default class PjpCartesianChartDataBuilder extends PjpChartDataBuilder {
    metricGraph: PjpMetricGraph;
    filters: Object;
    metric: PjpGraphResponse;
    domainAxisKey: string;
    rangeAxisKey: string;
    groupByToStack: string;

    constructor(
        metricGraph: PjpMetricGraph,
        filters: Object,
        metric: PjpGraphResponse,
        state: any,
        props: any
    ) {
        super(state, props);
        this.metricGraph = metricGraph;
        this.filters = filters;
        this.metric = metric;
    }

    auto() {
        const graphQuery = this.metricGraph.getGraphQuery();
        this.rangeAxisKey = graphQuery.metric;
        const firstMultiDimensionedGroupBy = graphQuery.groupBy.find(
            groupBy => {
                return PjpMetricCardDecider.isGroupByVariable(
                    this.metricGraph,
                    groupBy,
                    this.filters
                );
            }
        );
        if (firstMultiDimensionedGroupBy) {
            this.domainAxisKey = firstMultiDimensionedGroupBy;
        } else {
            this.domainAxisKey = this.rangeAxisKey;
        }
        // deciding stack
        if (!this.groupByToStack) {
            const relevantGroupByes = this.getDataGroupByes();
            const { stacks } = this.metricGraph;
            if (stacks) {
                /*
                    If stacks is present and is an empty array, then it means no stacks,
                    if it has multiple groupbyes, take the first one and group by it.
                    more than 1 stack not supported now.
                */
                if (stacks.size) {
                    const [firstStack] = stacks;
                    if (relevantGroupByes.indexOf(firstStack) < 0) {
                        throw new Error(
                            `groupby ${firstStack} is not a found or is not a variable data groupby`
                        );
                    }
                    this.groupByToStack = firstStack;
                }
            } else {
                if (relevantGroupByes.length > 0) {
                    //As of now the last relevant group by will be converted into stack, the prev (if any) into bars;
                    this.groupByToStack =
                        relevantGroupByes[relevantGroupByes.length - 1];
                }
            }
        }
        return this;
    }

    getTickType(axisKey: string) {
        const graphQuery = this.metricGraph.getGraphQuery();
        const metricName = graphQuery.metric;
        if (axisKey === this.rangeAxisKey) {
            return 'NUMBER';
        }
        return this.getGroupByTypeForMetric(axisKey, metricName);
    }

    deriveDomainAxis() {
        if (!this.domainAxisKey) {
            throw new Error('domainAxisKey not set!');
        }
        return {
            tickType: this.getTickType(this.domainAxisKey),
            key: this.domainAxisKey,
            label:
                this.domainAxisKey === this.rangeAxisKey
                    ? this.getLabelForMetric(this.domainAxisKey)
                    : this.getLabelForGroupBy(
                          this.domainAxisKey,
                          this.rangeAxisKey
                      )
        };
    }

    deriveRangeAxis() {
        if (!this.rangeAxisKey) {
            throw new Error('rangeAxisKey not set!');
        }
        return {
            label: this.getLabelForMetric(this.rangeAxisKey)
        };
    }

    deriveStacks(metricDataItems: List<PjpMetricDataItem>) {
        const stacks = {};
        if (!this.groupByToStack) {
            return stacks;
        }
        const relevantGroupByes = this.getDataGroupByes();
        const stackIndex = relevantGroupByes.indexOf(this.groupByToStack);
        if (stackIndex === 0 && relevantGroupByes.length === 1) {
            stacks[this.groupByToStack] = {
                source: 'GROUP_BY',
                label: this.getLabelForGroupBy(
                    this.groupByToStack,
                    this.rangeAxisKey
                )
            };
        } else {
            /*
            If we want to stack by a particular group by, we need to create groups of values of the other group by.
            For example, for groupByes TASK_STATUS and TEAM, if we want to stack by TASK_STATUS,
            we have to create groups for each stack.
            */
            const [groupByToFindValues] = _.difference(relevantGroupByes, [
                this.groupByToStack
            ]);
            metricDataItems.forEach(item => {
                const { metricDataRow } = item;
                const metricValue = metricDataRow.get(groupByToFindValues);
                if (metricValue) {
                    if (!stacks[metricValue.value]) {
                        const {
                            label,
                            color
                        } = this.getColorAndLabelForGroupByValue(
                            metricValue.value,
                            groupByToFindValues,
                            this.rangeAxisKey
                        );
                        stacks[metricValue.value] = {
                            source: 'VALUE',
                            groupBy: groupByToFindValues,
                            label,
                            color
                        };
                    }
                }
            });
        }
        return stacks;
    }

    deriveDataKeys(
        metricDataItems: List<PjpMetricDataItem>,
        stacks: StacksType
    ) {
        const colorPicker = new DifferentiableColorPicker();
        const relevantGroupByes = this.getDataGroupByes();
        const groupByValuesTree = {};
        // Creates a tree of all the keys in the metricDataItems
        metricDataItems.forEach(item => {
            const { metricDataRow } = item;
            const path = relevantGroupByes.reduce(
                (reduction: Array<string>, groupBy) => {
                    const metricValue = metricDataRow.get(groupBy);
                    if (metricValue) {
                        reduction.push((metricValue.value: string));
                    }
                    return reduction;
                },
                []
            );
            _.set(groupByValuesTree, path, {});
        });
        const flattenedTree = this.flattenTree(groupByValuesTree);
        const dataKeys = flattenedTree.map(branch => {
            let stackId = null;
            Object.keys(stacks).forEach(stackKey => {
                const stack = stacks[stackKey];
                if (stack.source === 'GROUP_BY') {
                    if (stackKey === relevantGroupByes[branch.length - 1]) {
                        stackId = relevantGroupByes[branch.length - 1];
                    }
                } else if (stack.source === 'VALUE') {
                    const indexOfGroupBy = relevantGroupByes.indexOf(
                        stack.groupBy
                    );
                    if (indexOfGroupBy >= 0) {
                        const value = branch[indexOfGroupBy];
                        if (stackKey === value) {
                            stackId = stackKey;
                        }
                    } else {
                        throw new Error(`Unknown group by: ${stack.groupBy}`);
                    }
                }
            });
            let value;
            let color;
            let label;
            if (stackId) {
                const stack = stacks[stackId];
                if (stack.source === 'VALUE') {
                    const indexOfGroupBy = relevantGroupByes.indexOf(
                        stack.groupBy
                    );
                    const otherGroupByes = relevantGroupByes.filter(
                        (elem, index) => indexOfGroupBy !== index
                    );
                    const lastGroupBy = _.last(otherGroupByes);
                    const indexInOriginalList = relevantGroupByes.indexOf(
                        lastGroupBy
                    );
                    value = branch[indexInOriginalList];
                    ({ label, color } = this.getColorAndLabelForGroupByValue(
                        value,
                        lastGroupBy,
                        this.rangeAxisKey
                    ));
                    if (!color) {
                        color = colorPicker.pick().code;
                    }
                }
            }
            if (!value) {
                value = _.last(branch);
                ({ label, color } = this.getColorAndLabelForGroupByValue(
                    value,
                    relevantGroupByes[branch.length - 1],
                    this.rangeAxisKey
                ));
                if (!color) {
                    color = colorPicker.pick().code;
                }
            }

            return {
                key: branch.join('-'),
                value,
                label,
                color,
                stackId
            };
        });
        if (dataKeys.length > 0) {
            // Need to sort data keys
            const [firstBranch] = flattenedTree;
            const groupBy = relevantGroupByes[firstBranch.length - 1];
            const staticValues = this.getStaticValuesOnlyForGroupBy(
                groupBy,
                this.rangeAxisKey
            );
            dataKeys.sort((first, second) => {
                const firstIndex = staticValues.indexOf((first.value: string));
                const secondIndex = staticValues.indexOf(second.value);
                if (firstIndex < 0 || secondIndex < 0) {
                    return 0;
                }
                return firstIndex - secondIndex;
            });
        }
        if (dataKeys.length === 0) {
            // For univariate data
            dataKeys.push({
                key: this.rangeAxisKey,
                label: this.getLabelForMetric(this.rangeAxisKey)
            });
        }
        return dataKeys;
    }

    getDataGroupByes() {
        const graphQuery = this.metricGraph.getGraphQuery();
        const relevantGroupByes: Array<string> = [];
        graphQuery.groupBy.forEach(groupByType => {
            if (
                groupByType !== this.domainAxisKey &&
                groupByType !== this.rangeAxisKey
            ) {
                if (
                    PjpMetricCardDecider.isGroupByVariable(
                        this.metricGraph,
                        groupByType,
                        this.filters
                    )
                ) {
                    relevantGroupByes.push(groupByType);
                }
            }
        });
        return relevantGroupByes;
    }

    deriveChartDataValues(metricDataItems: List<PjpMetricDataItem>) {
        const revelantGroupByes = this.getDataGroupByes();
        const dataValues = new Map();
        metricDataItems.forEach(dataItem => {
            const { metricDataRow } = dataItem;
            const metricValue = metricDataRow.get(this.domainAxisKey);
            var domainAxisValue;
            if (metricValue) {
                domainAxisValue = metricValue.value;
                if (this.domainAxisKey !== this.rangeAxisKey) {
                    domainAxisValue = this.getColorAndLabelForGroupByValue(
                        domainAxisValue,
                        this.domainAxisKey,
                        this.rangeAxisKey
                    ).label;
                }
                if (!dataValues.has(domainAxisValue)) {
                    dataValues.set(domainAxisValue, {
                        [this.domainAxisKey]: domainAxisValue
                    });
                }
            }
            const rangeAxisValue =
                metricDataRow.get(this.rangeAxisKey)?.value || 0;
            let metricDataKey = revelantGroupByes
                .reduce((reduction, groupBy) => {
                    const metricValue = metricDataRow.get(groupBy);
                    if (metricValue) {
                        reduction.push(metricValue.value);
                    }
                    return reduction;
                }, [])
                .join('-');
            if (!metricDataKey) {
                // For univariate data
                metricDataKey = this.rangeAxisKey;
            }
            if (domainAxisValue) {
                const domainDataValue = dataValues.get(domainAxisValue);
                if (domainDataValue) {
                    //This should always be true, but stupid flow does not understand.
                    domainDataValue[metricDataKey] = NumberUtils.roundOff(
                        rangeAxisValue
                    );
                }
            }
        });
        return Array.from(dataValues.values());
    }

    deriveRefLines() {
        const lines = [];
        const { refLines } = this.metricGraph;
        if (!refLines) {
            return lines;
        }
        refLines.forEach(refLine => {
            switch (refLine.type) {
                case 'ABSOLUTE':
                    lines.push({
                        type: 'ABSOLUTE',
                        value: refLine.value,
                        label: refLine.label || refLine.value,
                        alwaysShow: refLine.alwaysShow
                    });
                    break;
                default:
                    // eslint-disable-next-line no-console
                    console.error('Unsupported RefLine type: ', refLine.type);
            }
        });
        return lines;
    }

    flattenTree(tree: Object, start: string = '') {
        var joinedStrings = [];
        var prefix = start;
        _.forEach(tree, (val, key) => {
            if (_.isEmpty(val)) {
                joinedStrings.push(prefix ? _.concat([prefix], key) : [key]);
            }
            const recursiveJoinedStrings = this.flattenTree(val, key);
            recursiveJoinedStrings.forEach(joinedString => {
                joinedStrings.push(
                    prefix ? _.concat([prefix], joinedString) : joinedString
                );
            });
        });
        return joinedStrings;
    }

    build(): PjpCartesianChartDataType {
        const chartData = {
            layout: 'VERTICAL',
            domainAxis: this.deriveDomainAxis(),
            rangeAxis: this.deriveRangeAxis(),
            dataKeys: [],
            stacks: {},
            refLines: [],
            values: []
        };
        const metricsData = this.getMetricsDataForGraph(
            this.metricGraph,
            this.metric
        );
        if (!metricsData) {
            return chartData;
        }
        chartData.stacks = this.deriveStacks(metricsData.metricDataItems);
        chartData.dataKeys = this.deriveDataKeys(
            metricsData.metricDataItems,
            chartData.stacks
        );
        chartData.values = this.deriveChartDataValues(
            metricsData.metricDataItems
        );
        chartData.refLines = [];
        return chartData;
    }
}
